import { BrowserModule } from '@angular/platform-browser';
import { LOCALE_ID, NgModule, Injector, CUSTOM_ELEMENTS_SCHEMA, isDevMode, DoBootstrap } from '@angular/core';
import { AppComponent } from './app.component';
import { CommonModule, DatePipe, registerLocaleData } from '@angular/common';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { PublicLandingComponent } from './components/bfpe-not-client/public-landing/public-landing.component';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import { OnboardingProductsStore } from './store/store';
import { EffectsArray } from './store/effects/index';
import { PepModule, PepSetDataService } from 'omnichannel2-commercial-platform-pep';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { CurrencyTypeComponent } from './components/bfpe-common/currency-type/currency-type.component';
import { PublicLandingForeignComponent } from './components/bfpe-not-client/public-landing-foreign/public-landing-foreign.component';
import { PublicLandingForeignJointComponent } from './components/bfpe-not-client/public-landing-foreign-joint/public-landing-foreign-joint.component';
import { RouterModule } from '@angular/router';
import { LoadingComponent } from './components/bfpe-common/loading/loading.component';
import { ServiceErrorComponent } from './components/bfpe-common/service-error/service-error.component';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { BsSecureServiceCallInterceptor } from './interceptors/bs-secure-service-call.interceptor';
import { SessionTimerInterceptor } from './interceptors/session-timer.interceptor';
import { IsValidateSession } from './interceptors/is-validate-session.interceptor';
import { EncryptionUtil } from './services/encryption-util';
import { ClientFlowComponent } from './components/bfpe-common/client-flow/client-flow.component';
import { NoClientFlowComponent } from './components/bfpe-common/no-client-flow/no-client-flow.component';
import { TermsDataPersonalComponent } from './components/bfpe-common/terms-data-personal/terms-data-personal.component';
import { TermsCmrComponent } from './components/bfpe-common/terms-cmr/terms-cmr.component';
import localePe from '@angular/common/locales/es-PE';
import localePeExtra from '@angular/common/locales/extra/de';
import { VuSetDataService, VuSecondFactorModule } from 'vu-second-factor';
import { SucursalDerivationComponent } from './components/bfpe-common/sucursal-derivation/sucursal-derivation.component';
import { NumbersOnlyInputDirective } from './shared/directives/numbersOnlyInput.directive';
import { TextInputDirective } from './shared/directives/textInput.directive';
import { TextOnlyInputDirective } from './shared/directives/textOnlyInput.directive';
import { createCustomElement } from '@angular/elements';
import { SessionService } from './services/session.service';
import { LoadingService } from './services/loading.service';
import { ContractSigningComponent } from './components/bfpe-not-client/contract-signing/contract-signing.component';
import { PrivateLandingComponent } from './components/bfpe-client/private-landing/private-landing.component';
import { FaceRecognitionComponent } from './components/bfpe-not-client/face-recognition/face-recognition.component';
import { TextInputWithAmpersandDirective } from './shared/directives/textInputWithAmpersand.directive';
import { PublicAddressComponent } from './components/bfpe-not-client/public-address/public-address.component';
import { PublicAddressJointComponent } from './components/bfpe-not-client/public-address-joint/public-address-joint.component';
import { ParentCallerService } from './services/parent-caller.service';
import { UpdateDataComponent } from './components/bfpe-common/update-data/update-data.component';
import { DataClientService } from './services/data-client.service';
import { DataNotClientService } from './services/data-not-client.service';
import { BsRefreshTokenInterceptor } from './interceptors/bs-refresh-token.interceptor';
import { AuthenticationService } from './services/authentication.service';
import { BsCallApiService } from './services/bs-call-api.service';
import { ClientStepConfig4MobileComponent } from './components/bfpe-client/client-step-config4-mobile/client-step-config4-mobile.component';
import { Analytics } from 'fif-common-analytics';
import { ContextService } from './services/context.service';
import { SetInitialSessionService } from './services/setInitialSessionService.service';
import { ContentfulService } from './services/contentful.service';
import { FaceIdHandler } from './components/bfpe-not-client/face-recognition/handler/face-id.handler';
import { FaceErrorComponent } from './components/bfpe-not-client/face-recognition/face-error/face-error.component';
import { HeaderComponent } from './components/bfpe-common/header/header.component';
import { PepComponent } from './components/bfpe-common/pep/pep.component';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { OfferHandler } from './components/bfpe-common/offer/handler/step-offer.handler';
import { OfferComponent } from './components/bfpe-common/offer/offer.component';
import { SuccessProcessComponent } from './components/bfpe-common/success-process/success-process.component';
import { AccountVoucherComponent } from './components/bfpe-common/account-voucher/account-voucher.component';
import { CommonModalComponent } from './components/bfpe-common/common-modal/common-modal.component';
import { ClientStepSoftokenVuComponent } from './components/bfpe-client/client-step-softoken-vu/client-step-softoken-vu.component';
import { PepFormComponent } from './components/bfpe-common/pep-form/pep-form.component';
import { LabourInformationComponent } from './components/bfpe-common/labour-information/labour-information.component';
import { LabourInformationJointComponent } from './components/bfpe-common/labour-information-joint/labour-information-joint.component';
import { environment } from '../environments/environment';
import { TermsConditionsComponent } from './components/bfpe-common/terms-conditions/terms-conditions.component';
import { NationalityComponent } from './components/bfpe-common/nationality/nationality.component';
import { NationalityJointComponent } from './components/bfpe-common/nationality-joint/nationality-joint.component';
import { NoOfferComponent } from './components/bfpe-common/no-offer/no-offer.component';
import { DerivationComponent } from './components/bfpe-common/derivation/derivation.component';
import { CreateProductComponent } from './components/bfpe-common/create-product/create-product.component';
import { PepFormJointComponent } from './components/bfpe-common/pep-form-joint/pep-form-joint.component';
import { SuccessProcessHandler } from './components/bfpe-common/success-process/handler/success-process.handler';
import { LabourHandler } from './components/bfpe-common/labour-information/handler/labour.handler';
import { MaintenanceComponent } from './components/bfpe-common/maintenance/maintenance.component';
import { TermsConditionsHandler } from './components/bfpe-common/terms-conditions/handler/terms-conditions.handler';
import { CreateProductJointComponent } from './components/bfpe-common/create-product-joint/create-product-joint.component';
import { QRCodeModule } from 'angularx-qrcode';
import { CreatePasswordComponent } from './components/bfpe-not-client/create-password/create-password.component';
import { CapiService } from './services/capi/capi.service';
import { AbrLightComponent } from './components/bfpe-not-client/abr-light/abr-light.component';
import { SignalService } from './services/signals/signal.service';


registerLocaleData(localePe, 'es-PE', localePeExtra);


@NgModule({
    declarations: [
        AppComponent,
        LabourInformationComponent,
        LabourInformationJointComponent,
        NoOfferComponent,
        PepFormComponent,
        PublicLandingForeignJointComponent,
        PublicAddressJointComponent,
        DerivationComponent,
        MaintenanceComponent,
        PepFormJointComponent,
        LoadingComponent,
        ServiceErrorComponent,
        ClientFlowComponent,
        NoClientFlowComponent,
        ClientStepConfig4MobileComponent,
        CreatePasswordComponent,
        SuccessProcessComponent,
        TermsDataPersonalComponent,
        TermsCmrComponent,
        AccountVoucherComponent,
        SucursalDerivationComponent,
        NumbersOnlyInputDirective,
        TextInputDirective,
        TextOnlyInputDirective,
        TextInputWithAmpersandDirective,
        ContractSigningComponent,
        PrivateLandingComponent,
        UpdateDataComponent,
        ClientStepSoftokenVuComponent,
        FaceRecognitionComponent,
        FaceErrorComponent,
        HeaderComponent,
        PepComponent,
        OfferComponent,
        CurrencyTypeComponent,
        CommonModalComponent,
        PublicLandingComponent,
        PublicAddressComponent,
        CreateProductComponent,
        PublicLandingForeignComponent,
        TermsConditionsComponent,
        NationalityComponent,
        NationalityJointComponent,
        CreateProductJointComponent,
        AbrLightComponent
    ],
    imports: [
        BrowserModule,
        CommonModule,
        FormsModule,
        QRCodeModule,
        ReactiveFormsModule,
        NgbTooltipModule,
        StoreModule.forRoot(OnboardingProductsStore.appReducers),
        EffectsModule.forRoot(EffectsArray),
        RouterModule.forRoot([]),
        HttpClientModule,
        PepModule.forRoot(environment),
        VuSecondFactorModule.forRoot(environment),
        StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: !isDevMode() })
    ],
    providers: [
        { provide: LOCALE_ID, useValue: 'es-PE' },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: BsSecureServiceCallInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: BsRefreshTokenInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: SessionTimerInterceptor,
            multi: true
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: IsValidateSession,
            multi: true
        },
        EncryptionUtil,
        AuthenticationService,
        BsCallApiService,
        SessionService,
        LoadingService,
        PepSetDataService,
        DatePipe,
        VuSetDataService,
        Analytics.TaggingAdapter,
        ParentCallerService,
        DataClientService,
        DataNotClientService,
        CapiService,
        SignalService,
        ContextService,
        SetInitialSessionService,
        ContentfulService,
        FaceIdHandler,
        OfferHandler,
        SuccessProcessHandler,
        TermsConditionsHandler,
        LabourHandler
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})

export class AppModule implements DoBootstrap {
  constructor(private injector: Injector) {
    const el = createCustomElement(AppComponent, { injector: this.injector });
    customElements.define('wc-onboarding', el);
  }
  ngDoBootstrap() { }
}
